<template>
  <div :class="isHero ? 'absolute inset-0 size-full' : 'relative w-full'">
    <RokkaImage
      v-if="videoMobile?.file?.rokka?.hash"
      :hash="videoMobile?.file?.rokka?.hash || ''"
      :config="imageStyle"
      preload
      loading="eager"
      class="absolute top-0 left-0 size-full xs:hidden"
      :source-width="videoMobile?.file?.rokka?.sourceWidth"
      :source-height="videoMobile?.file?.rokka?.sourceHeight"
      :alt="$texts('accessibility.header_picture')"
      aria-hidden="true"
    />
    <ClientOnly>
      <MediaVideoPlayer
        v-if="source"
        :key="source.src"
        ref="mediaVideoPlayer"
        :poster="source.poster"
        :sources="[
          {
            src: videoSrc ?? source.src,
            type: 'application/x-mpegURL',
          },
        ]"
        :autoplay="autoplay"
        :loop="loop"
        :class="
          isHero
            ? 'absolute inset-0 size-full'
            : 'absolute top-0 left-0 size-full'
        "
        :controls="controls"
        :muted="muted"
        :video-class="videoClass"
        :is-landing="isLanding"
        :is-hero="isHero"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { RokkaImage } from '#components'
import type { DefineImageStyleConfig } from '#rokka/types'
import type { MediaVideoPlayer } from '#build/components'
import type { MediaVideoFragment, VideoItemFragment } from '#graphql-operations'
import { getRokkaUrl } from '~/composables/getRokkaUrl'

const { isMobile } = useViewport()

type MediaVideoProps = MediaVideoFragment & {
  autoplay?: boolean
  loop?: boolean
  controls?: boolean
  videoClass?: string
  video: MediaVideoFragment['video']
  videoMobile?: MediaVideoFragment['videoMobile']
  muted?: boolean
  videoSrc?: string
  imageStyle: DefineImageStyleConfig
  isLanding?: boolean
  poster?: MediaVideoFragment['poster']
  isHero?: boolean
}

const props = defineProps<MediaVideoProps>()

type Source = {
  poster?: string
  src: string
}

function buildSource(video?: VideoItemFragment): Source | undefined {
  if (!video) {
    return
  }
  const hash = video.file?.rokka?.hash

  if (!hash) {
    return
  }

  const poster = props.poster?.file?.rokka?.hash
    ? getRokkaUrl(props.poster.file.rokka.hash)
    : getRokkaUrl(hash)
  const src = getRokkaUrl(hash, 'm3u')

  if (!src) {
    return
  }

  return {
    poster,
    src,
  }
}

const sourceDesktop = computed<Source | undefined>(() =>
  buildSource(props.video),
)
const sourceMobile = computed<Source | undefined>(() =>
  buildSource(props.videoMobile),
)

// Initialize with desktop source (will be updated on client if mobile)
const currentSource = ref<Source | undefined>(sourceDesktop.value)

// Update source based on mobile detection
const updateSource = () => {
  if (import.meta.client && isMobile.value && sourceMobile.value) {
    currentSource.value = sourceMobile.value
  } else if (sourceDesktop.value) {
    currentSource.value = sourceDesktop.value
  }
}

// Watch for changes in mobile detection or source availability
watch(
  [isMobile, sourceMobile, sourceDesktop],
  () => {
    updateSource()
  },
  { immediate: true },
)

// Also update on mount to ensure client-side detection works
onMounted(() => {
  updateSource()
})

const source = computed(() => currentSource.value)

const mediaVideoPlayer = ref<typeof MediaVideoPlayer>()
const play = () => {
  if (mediaVideoPlayer.value) {
    mediaVideoPlayer.value.play()
  }
}

defineExpose({
  play,
})

defineOptions({
  name: 'MediaVideo',
})
</script>
